import ErrorBoundary from '@ui/error'
import React, {useState} from 'react'
import {useNavigate} from 'react-router-dom'
import S from '../components/auth/styles/Form.styles'
import SubmitButton from '../components/auth/SubmitButton'
import Layout from '../components/layout/Layout'
import Dropdown, {useDropdown} from '../components/ui/dropdown-nums'
import {cognitoToDisplay} from '../models/role'
import {roleApi} from '../services'
import {useCurrentUser} from '../store/slice-hooks'
import {setBackendRolePermissions, updateLoggedInAgencyId} from '../store/slices/root-slice'
import {useAppDispatch} from '../store/store-hooks'
import {useLazyGetGaAgencyNamesAndIdsQuery} from '../store/apis/member-access-api'

/**
 * Module permission interface for type checking
 */
interface ModulePermission {
  key: string
  name: string
  canView: boolean
  canCreate: boolean
  canEdit: boolean
  canDelete: boolean
  submodules?: ModulePermission[]
}

/**
 * Get the landing route for a role based on permissions
 * Priority: dashboard > first available module
 * URLs no longer contain role prefixes - all routes are at root level
 * The layout variant is determined from user role in AppRoutes, not from URL
 */
const getRouteForRole = (roleName: string, modulePermissions?: ModulePermission[]): string => {
  // Check if user has dashboard permission
  if (modulePermissions) {
    const dashboardPermission = modulePermissions.find((m) => m.key === 'dashboard')
    if (dashboardPermission?.canView) {
      return '/dashboard'
    }

    // Check for pre_fill permission (MSP/Agency roles)
    const preFillPermission = modulePermissions.find((m) => m.key === 'pre_fill')
    if (preFillPermission?.canView) {
      return '/prefill'
    }
  }

  const lowerRoleName = roleName.toLowerCase()

  // Company roles without dashboard land on manage-orders
  if (lowerRoleName === 'companyadmin' || lowerRoleName === 'companyuser') {
    return '/manage-orders'
  }

  // All other roles without dashboard land on manage-companies
  return '/manage-companies'
}

export default function RoleSelectPage(): JSX.Element {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const {currentUser} = useCurrentUser()
  const {getDropdownProps, optionIdx} = useDropdown()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [getGaAgencyNamesAndIds] = useLazyGetGaAgencyNamesAndIdsQuery()

  const handleRoleSelect = async () => {
    if (optionIdx < 0 || !currentUser) return

    try {
      setIsLoading(true)
      setError(null)
      const roleName = currentUser.roles[optionIdx] as string

      // Fetch permissions for the selected role and store in Redux
      const permissionsResponse = await roleApi.getPermissionsForRoles([roleName])
      const primaryRole = permissionsResponse.roles[0]

      if (!primaryRole) {
        navigate('/manage-companies')
        return
      }

      // For multi-agency users, check if the first agency has custom permissions
      // If so, use those instead of role defaults
      const firstAgencyPermissions = currentUser.agencyAssignments?.[0]?.modulePermissions
      const hasAgencyPermissions = firstAgencyPermissions && firstAgencyPermissions.length > 0

      // Store role permissions in Redux
      dispatch(
        setBackendRolePermissions({
          _id: primaryRole.id,
          id: primaryRole.id,
          name: primaryRole.name,
          accessList: primaryRole.accessList,
          // Use agency-specific permissions if available, otherwise role defaults
          modulePermissions: hasAgencyPermissions
            ? firstAgencyPermissions
            : primaryRole.modulePermissions,
          isPlatform: primaryRole.category === 'Platform',
          // Store category for layout variant determination (custom roles need this)
          category: primaryRole.category as 'Platform' | 'MSP' | 'Agency' | 'Company',
          application: 'msp',
          createdDate: primaryRole.createdDate?.toString() ?? '',
          modifiedDate: primaryRole.modifiedDate?.toString() ?? '',
          versionKey: Number(primaryRole.versionKey) || 0,
        }),
      )

      // Store role in localStorage for compatibility
      window.localStorage.setItem('cognitoRole', JSON.stringify({role: primaryRole}))

      // Handle agency selection based on user type:
      // 1. Multi-agency users (agencyAssignments): auto-select the first agency
      // 2. General admin users: fetch their agency list from member access
      const hasMultiAgencyAssignments =
        currentUser.agencyAssignments && currentUser.agencyAssignments.length > 0

      if (hasMultiAgencyAssignments) {
        // Multi-agency user: auto-select the first agency assignment
        const firstAgency = currentUser.agencyAssignments![0]
        dispatch(updateLoggedInAgencyId(firstAgency.agencyId))
      } else if (primaryRole.name.toLowerCase() === 'general_admin') {
        await loadGeneralAdminAgencies(currentUser.id)
      }

      // Get route based on role NAME and permissions
      // Use agency-specific permissions for routing if available
      const effectivePermissions = hasAgencyPermissions
        ? (firstAgencyPermissions as ModulePermission[])
        : (primaryRole.modulePermissions as ModulePermission[])
      const targetRoute = getRouteForRole(primaryRole.name, effectivePermissions)
      navigate(targetRoute)
    } catch (err) {
      setError(err as Error)
    } finally {
      setIsLoading(false)
    }
  }

  const loadGeneralAdminAgencies = async (userId: string) => {
    try {
      const agencies = await getGaAgencyNamesAndIds(userId).unwrap()
      if (agencies.length > 0) {
        dispatch(updateLoggedInAgencyId(agencies[0].id))
      }
    } catch (agencyError) {
      console.error('Failed to fetch agencies for general_admin:', agencyError)
    }
  }

  return (
    <ErrorBoundary error={error}>
      <Layout variant="platform">
        <S.MaxWidth>
          <S.InnerPaper elevation={6}>
            <S.Greeting data-testid="greeting" />
            <S.InnerBox>
              <S.Container
                onSubmit={async (e: React.FormEvent) => {
                  e.preventDefault()
                  await handleRoleSelect()
                }}>
                <Dropdown
                  {...getDropdownProps({
                    style: {width: '100%'},
                    optionIdx: optionIdx,
                    placeholder: '-- Select Role --',
                    'data-testid': 'role-select-dropdown',
                    options: currentUser?.roles.map(cognitoToDisplay) ?? [],
                  })}
                />
                <SubmitButton disabled={isLoading}>Select Role</SubmitButton>
              </S.Container>
            </S.InnerBox>
          </S.InnerPaper>
        </S.MaxWidth>
      </Layout>
    </ErrorBoundary>
  )
}
